import React, { useEffect, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Star, StarHalf, Download, Image as ImageIcon, UploadSimple } from "@phosphor-icons/react";

const BLANK = {
  title: "", slug: "", category: "Operations", short_description: "",
  problem: "", solution: "", target_user: "", features: [], media_url: "",
  screens: [], status: "published", featured: false, tags: [],
};

export default function Demos() {
  const [demos, setDemos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [managing, setManaging] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/demos");
      setDemos(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...editing,
        features: typeof editing.features === "string" ? editing.features.split("\n").filter(Boolean) : editing.features,
        tags: typeof editing.tags === "string" ? editing.tags.split(",").map((t) => t.trim()).filter(Boolean) : editing.tags,
      };
      if (editing.id) {
        await api.put(`/admin/demos/${editing.id}`, payload);
      } else {
        await api.post("/admin/demos", payload);
      }
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this demo?")) return;
    await api.delete(`/admin/demos/${id}`);
    toast.success("Deleted");
    await load();
  };

  const featuredCount = demos.filter((d) => d.featured).length;
  const toggleFeatured = async (d) => {
    if (!d.featured && featuredCount >= 3) {
      toast.error("Only 3 demos can be featured at once. Un-feature another first.");
      return;
    }
    try {
      await api.put(`/admin/demos/${d.id}`, { ...d, featured: !d.featured });
      toast.success(d.featured ? "Removed from homepage" : "Featured on homepage");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
    }
  };

  return (
    <div className="p-8 md:p-10" data-testid="admin-demos">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="overline mb-2">Demo management</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Demos</h1>
          <p className="text-xs text-zinc-500 mt-1 font-mono">
            <span className={featuredCount === 3 ? "text-emerald-600" : "text-amber-600"}>{featuredCount}/3</span> featured on the homepage — click the star to toggle.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`${API_BASE}/admin/demos.csv`}
            className="btn-ghost-ink !py-2"
            data-testid="demos-export-csv"
            title="Export every demo with engagement metrics (IdeaLab email clicks + direct interest submissions)"
          >
            <Download size={14} weight="bold" /> Export CSV
          </a>
          <button onClick={() => setEditing({ ...BLANK })} className="btn-signal" data-testid="demos-create">
            <Plus size={16} weight="bold" /> New demo
          </button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              {["Title", "Slug", "Category", "Status", "Featured", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="p-8 text-center text-zinc-500 font-mono">Loading…</td></tr> :
              demos.map((d) => (
                <tr key={d.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`demo-row-${d.id}`}>
                  <td className="px-4 py-3 font-medium">{d.title}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-600">{d.slug}</td>
                  <td className="px-4 py-3">{d.category}</td>
                  <td className="px-4 py-3"><span className="status-pill">{d.status}</span></td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleFeatured(d)}
                      className={`flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest ${d.featured ? "text-amber-600 hover:text-amber-700" : "text-zinc-400 hover:text-zinc-700"}`}
                      data-testid={`demo-feature-${d.id}`}
                      title={d.featured ? "Currently featured on the homepage — click to remove" : "Click to feature on the homepage (max 3)"}
                    >
                      <Star size={16} weight={d.featured ? "fill" : "regular"} />
                      {d.featured ? "Featured" : "Feature"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setManaging(d)} className="text-zinc-600 hover:text-zinc-900 mr-3" data-testid={`demo-images-${d.id}`} title="Manage images">
                      <ImageIcon size={16} />
                    </button>
                    <button onClick={() => setEditing({ ...d, features: (d.features || []).join("\n"), tags: (d.tags || []).join(", ") })} className="text-zinc-600 hover:text-zinc-900 mr-3" data-testid={`demo-edit-${d.id}`}><PencilSimple size={16} /></button>
                    <button onClick={() => remove(d.id)} className="text-zinc-600 hover:text-red-600" data-testid={`demo-delete-${d.id}`}><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="demo-editor">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="font-semibold">{editing.id ? "Edit demo" : "New demo"}</h2>
              <button onClick={() => setEditing(null)} className="text-zinc-500 text-xl leading-none">×</button>
            </div>
            <div className="p-6 space-y-4">
              <Input label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="demo-title-input" />
              <Input label="Slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} testid="demo-slug-input" />
              <Input label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
              <Input label="Target user" value={editing.target_user} onChange={(v) => setEditing({ ...editing, target_user: v })} />
              <Input label="Short description" value={editing.short_description} onChange={(v) => setEditing({ ...editing, short_description: v })} />
              <Textarea label="Problem" value={editing.problem} onChange={(v) => setEditing({ ...editing, problem: v })} />
              <Textarea label="Solution" value={editing.solution} onChange={(v) => setEditing({ ...editing, solution: v })} />
              <Textarea label="Features (one per line)" value={editing.features} onChange={(v) => setEditing({ ...editing, features: v })} />
              <Input label="Tags (comma separated)" value={editing.tags} onChange={(v) => setEditing({ ...editing, tags: v })} />
              <Input label="Media URL" value={editing.media_url || ""} onChange={(v) => setEditing({ ...editing, media_url: v })} />
              <Input label="Live URL (opens in a new tab from the case-study page)" value={editing.live_url || ""} onChange={(v) => setEditing({ ...editing, live_url: v })} placeholder="https://your-live-app.com" />
              <div className="grid grid-cols-2 gap-4">
                <label className="text-sm">Status
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="block w-full mt-1 border border-zinc-200 px-3 py-2 bg-white">
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </label>
                <label className="text-sm flex items-end gap-2">
                  <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured on homepage
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-ghost-ink">Cancel</button>
              <button onClick={save} className="btn-signal" data-testid="demo-save">Save demo</button>
            </div>
          </div>
        </div>
      )}

      {managing && (
        <DemoImagesModal
          demo={managing}
          onClose={() => setManaging(null)}
          onRefresh={async () => { await load(); /* keep modal open showing the just-refreshed demo */ const fresh = (await api.get("/admin/demos")).data.find((x) => x.id === managing.id); if (fresh) setManaging(fresh); }}
        />
      )}
    </div>
  );
}

function DemoImagesModal({ demo, onClose, onRefresh }) {
  const galleries = [
    { key: "media_url", label: "Hero / card image", items: demo.media_url ? [{ url: demo.media_url, caption: "Shown on demo cards and the case-study header", alt: demo.media_alt || "" }] : [] },
    { key: "screens", label: "Main carousel (resident-facing)", items: demo.screens || [] },
    { key: "vendor_screens", label: "Vendor / sponsor gallery", items: demo.vendor_screens || [] },
    { key: "admin_screens", label: "Inside the app (admin gallery)", items: demo.admin_screens || [] },
  ];
  const [uploading, setUploading] = useState(null);
  // alt-text editor state: per-card local draft + per-card busy flags
  const initialAlts = {};
  galleries.forEach((g) => {
    g.items.forEach((item, i) => {
      const idx = g.key === "media_url" ? -1 : i;
      initialAlts[`${g.key}.${idx}`] = item.alt || "";
    });
  });
  const [altDrafts, setAltDrafts] = useState(initialAlts);
  const [altBusy, setAltBusy] = useState(null); // `${gallery}.${index}.gen` | `.save`

  const replace = async (gallery, index, file) => {
    if (!file) return;
    const key = `${gallery}.${index}`;
    setUploading(key);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("gallery", gallery);
      form.append("index", String(index));
      await api.post(`/admin/demos/${demo.id}/images`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image replaced");
      await onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const generateAlt = async (gallery, index, caption) => {
    const key = `${gallery}.${index}`;
    setAltBusy(`${key}.gen`);
    try {
      const { data } = await api.post(`/admin/demos/${demo.id}/images/alt-text`, { gallery, index, caption });
      setAltDrafts((d) => ({ ...d, [key]: data.alt || "" }));
      toast.success("Alt text generated — review and Save");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not generate alt text");
    } finally {
      setAltBusy(null);
    }
  };

  const saveAlt = async (gallery, index) => {
    const key = `${gallery}.${index}`;
    setAltBusy(`${key}.save`);
    try {
      await api.patch(`/admin/demos/${demo.id}/images/alt-text`, { gallery, index, alt: altDrafts[key] || "" });
      toast.success("Alt text saved");
      await onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setAltBusy(null);
    }
  };

  const resolveUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/")) return `${API_BASE}${url.slice(4)}`;
    return url;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="demo-images-modal">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <p className="overline mb-0.5">Images · {demo.slug}</p>
            <h2 className="font-semibold text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>{demo.title}</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 text-2xl leading-none px-2" data-testid="demo-images-close">×</button>
        </div>

        <div className="p-6 space-y-8">
          <p className="text-sm text-zinc-600">
            Captions stay the same. Just replace the image and the new version goes live immediately. Max 8 MB per image. JPEG, PNG, WEBP, GIF, HEIC or SVG.
          </p>

          {galleries.map((g) => (
            <div key={g.key} data-testid={`demo-images-gallery-${g.key}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm">{g.label}</h3>
                <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{g.items.length} image{g.items.length === 1 ? "" : "s"}</span>
              </div>

              {g.items.length === 0 ? (
                <p className="text-sm text-zinc-400 italic mb-2">No images in this gallery yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {g.items.map((item, i) => {
                    const idx = g.key === "media_url" ? -1 : i;
                    const isUp = uploading === `${g.key}.${idx}`;
                    const altKey = `${g.key}.${idx}`;
                    const altDraft = altDrafts[altKey] ?? (item.alt || "");
                    const altGenBusy = altBusy === `${altKey}.gen`;
                    const altSaveBusy = altBusy === `${altKey}.save`;
                    const altDirty = (altDraft || "") !== (item.alt || "");
                    return (
                      <div key={`${g.key}-${i}`} className="border border-zinc-200 overflow-hidden" data-testid={`demo-images-item-${g.key}-${i}`}>
                        <div className="aspect-video bg-zinc-100 relative">
                          {item.url ? (
                            <img src={resolveUrl(item.url)} alt={item.alt || item.caption || ""} className="w-full h-full object-cover" />
                          ) : null}
                          {isUp && (
                            <div className="absolute inset-0 bg-zinc-950/70 text-white text-xs font-mono uppercase tracking-widest flex items-center justify-center">
                              Uploading…
                            </div>
                          )}
                        </div>
                        <div className="p-3 space-y-3">
                          <p className="text-xs text-zinc-600 line-clamp-2">{item.caption || "—"}</p>
                          <label className="block">
                            <span className="sr-only">Replace image</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => replace(g.key, idx, e.target.files?.[0])}
                              className="hidden"
                              data-testid={`demo-images-input-${g.key}-${i}`}
                              disabled={isUp}
                            />
                            <span className="btn-ghost-ink !py-1.5 !text-xs w-full justify-center cursor-pointer">
                              <UploadSimple size={12} weight="bold" /> Replace image
                            </span>
                          </label>

                          {/* Alt text editor */}
                          <div className="pt-2 border-t border-zinc-100">
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Alt text · accessibility</label>
                              <span className={`text-[10px] font-mono ${(altDraft || "").length > 125 ? "text-red-600" : "text-zinc-400"}`}>{(altDraft || "").length}/125</span>
                            </div>
                            <textarea
                              rows={2}
                              value={altDraft}
                              onChange={(e) => setAltDrafts((d) => ({ ...d, [altKey]: e.target.value }))}
                              placeholder={item.alt ? "" : (item.caption ? "Click Generate to draft alt text from the caption." : "")}
                              className="w-full text-xs border border-zinc-200 px-2 py-1.5 mb-2 resize-y bg-white"
                              data-testid={`demo-images-alt-textarea-${g.key}-${i}`}
                            />
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => generateAlt(g.key, idx, item.caption || "")}
                                disabled={altGenBusy || !item.caption}
                                className="btn-ghost-ink !py-1 !text-[11px] flex-1 justify-center disabled:opacity-50"
                                data-testid={`demo-images-alt-generate-${g.key}-${i}`}
                                title={item.caption ? "Use Claude to draft WCAG-compliant alt text from the caption" : "Add a caption first"}
                              >
                                {altGenBusy ? "Generating…" : "✨ Generate"}
                              </button>
                              <button
                                type="button"
                                onClick={() => saveAlt(g.key, idx)}
                                disabled={altSaveBusy || !altDirty}
                                className="btn-signal !py-1 !text-[11px] flex-1 justify-center disabled:opacity-50"
                                data-testid={`demo-images-alt-save-${g.key}-${i}`}
                              >
                                {altSaveBusy ? "Saving…" : "Save"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-signal" data-testid="demo-images-done">Done</button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block text-sm">
      <span className="font-semibold text-zinc-800 mb-1 block">{label}</span>
      <input value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="w-full border border-zinc-200 px-3 py-2 bg-white" data-testid={testid} />
    </label>
  );
}
function Textarea({ label, value, onChange }) {
  return (
    <label className="block text-sm">
      <span className="font-semibold text-zinc-800 mb-1 block">{label}</span>
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-zinc-200 px-3 py-2 bg-white" />
    </label>
  );
}
